/* eslint-disable import/no-cycle */
import {compact, isMatch, uniq} from 'lodash';
import {action, autorun, computed, decorate, observable, runInAction} from 'mobx';
import {ApiError} from '../models/apiError';
import i18n from '../i18n/i18n';

export const LoadStrategies = {
  replace: 'replace',
  add: 'add',
};

export const Actions = {
  load: 'load',
  /** Calls the method "all" of the given object. As example {@link UsersApi.all} */
  loadAll: 'all',
  delete: 'delete',
  create: 'create',
  update: 'update',
  byId: 'byId',
};

export class EntityStore {
  pendingActions = [];
  identityField = 'id';
  supportsMultiIdLoad = false;

  constructor(rootStore, listName, api, modelClass, supportsMultiIdLoad = false) {
    this.rootStore = rootStore;
    this.listName = listName;
    this.api = api;
    this.modelClass = modelClass;
    this.supportsMultiIdLoad = supportsMultiIdLoad;
    this.multiComparator = (ids) => (e) => ids
      .map((id) => id.toString())
      .indexOf(e[this.identityField].toString()) >= 0;
    this.comparator = (id) => this.multiComparator([id]);

    autorun(() => {
      // automatically set model relations from other stores,
      // runs whenever related/dependent store lists change
      if (!this[this.listName]) {
        return;
      }
      this[this.listName].forEach((e) => e.populateAttributesFromStore(this.rootStore));
    });
  }

  prepareApiPayload(model) {
    if (typeof this.modelClass.prepareApiPayload === 'function') {
      return this.modelClass.prepareApiPayload(model);
    }
    return model;
  }

  load(id, paramOptions = {}) {
    const defaultOptions = {
      params: {},
      onlyIfEmpty: false,
      action: Actions.load,
      raw: false,
      api: this.api,
      apiEndpoint: Actions.byId,
    };
    const options = Object.assign(defaultOptions, paramOptions, {[this.identityField]: id});
    if (this.isLoading(id) || (options.onlyIfEmpty && this.getById(id))) {
      return Promise.resolve(this.getById(id));
    }
    this.addPendingAction(options.action, options);

    return options.api[options.apiEndpoint](id, options).then(({data, ...rest}) => {
      runInAction(() => this.removePendingAction(options.action, options));
      if (options.raw) return {data, ...rest};

      if (rest.status === 204) {
        return undefined;
      }
      const entity = this.createModelInstance(data);
      const [added] = this.addAll([entity], {...options, replaceOptions: {preserveReduced: true}});
      return added;
    }).catch((e) => this.handleApiError(e, options.action, options));
  }

  loadManyBySingleLoad(ids, paramOptions = {}) {
    const defaultOptions = {onlyIfEmpty: true};
    const options = Object.assign(defaultOptions, paramOptions);

    if (Array.isArray(ids)) {
      // eslint-disable-next-line no-param-reassign
      ids = compact(uniq(ids));
    }
    return Promise.all((ids || []).map((id) => this.load(id, options)));
  }

  async loadMany(requestedIds, paramOptions = {}) {
    if (!this.supportsMultiIdLoad) {
      return this.loadManyBySingleLoad(requestedIds, paramOptions);
    }

    let ids = requestedIds;
    const defaultOptions = {
      params: {},
      onlyIfEmpty: false,
      action: Actions.load,
      raw: false,
      api: this.api,
      apiEndpoint: Actions.byId,
    };
    const options = Object.assign(defaultOptions, paramOptions);

    if (Array.isArray(ids)) {
      ids = compact(uniq(ids));
    }

    const idsToLoad = ids.filter((id) => !this.isLoading(id));

    if (idsToLoad.length === 1) {
      return this.load(idsToLoad[0]);
    }

    let result = [];

    if (idsToLoad.length > 0) {
      idsToLoad.forEach((id) => {
        this.addPendingAction(Actions.load, {
          ...options,
          onlyIfEmpty: false,
          [this.identityField]: id,
        });
      });
      const params = Object.assign(paramOptions.params || {}, {id: idsToLoad});
      result = await this.loadAll({params});
      idsToLoad.forEach((id) => {
        this.removePendingAction(Actions.load, {
          ...options,
          onlyIfEmpty: false,
          [this.identityField]: id,
        });
      });
    }

    return Promise.resolve(result);
  }

  loadAll(paramOptions = {}) {
    const defaultOptions = {
      strategy: LoadStrategies.add,
      params: {},
      onlyIfEmpty: false,
      api: this.api,
      raw: false,
      apiEndpoint: Actions.loadAll,
      mapper: this.createModelInstance.bind(this),
    };
    const options = Object.assign(defaultOptions, paramOptions);
    const pendingAction = options.apiEndpoint;

    if (this.isActionInProgress(pendingAction, options)
      || (options.onlyIfEmpty && !this.isEmpty)
    ) {
      // eslint-disable-next-line max-len,no-console
      console.error(`Warning: This request (${pendingAction}-${this.listName}) is already running. Another request was not sent, and no result will be returned. Change your loading-order / effect-dependencies to prevent this. Params: `, JSON.stringify(options.params));
      return Promise.resolve([]);
    }

    this.addPendingAction(pendingAction, options);

    return options.api[options.apiEndpoint](options.params).then(({data, ...rest}) => {
      let entities = options.raw
        ? {data, ...rest}
        : data.map(options.mapper);
      runInAction(() => {
        this.removePendingAction(pendingAction, options);

        if (options.raw) return;

        if (options.strategy === LoadStrategies.replace) {
          this[this.listName].replace(entities);
        } else if (options.strategy === LoadStrategies.add) {
          entities = this.addAll(entities, {...options, replaceOptions: {preserveReduced: true}});
        }
      });
      return entities;
    }).catch((e) => this.handleApiError(e, pendingAction, options));
  }

  delete(id, options = {}) {
    this.addPendingAction(Actions.delete);
    return this.api.destroy(id, options.params).then((response) => {
      // t('flashMessages.deleteSuccess')
      this.addMessage({type: 'success', title: 'flashMessages.deleteSuccess'}, options);
      runInAction(() => this.removePendingAction(Actions.delete));
      this.remove(id);
      return response;
    }).catch((e) => this.handleApiError(e, Actions.delete));
  }

  create(entity, options = {}) {
    const pendingAction = options.apiEndpoint || Actions.create;
    this.addPendingAction(pendingAction);
    return this.api[pendingAction](this.prepareApiPayload(entity), options.params).then(({data, ...rest}) => {
      // t('flashMessages.createSuccess')
      this.addMessage({type: 'success', title: 'flashMessages.createSuccess'}, options);
      runInAction(() => this.removePendingAction(pendingAction));
      if (options.raw) {
        return {data, ...rest};
      }
      return this.add(this.createModelInstance(data));
    }).catch((e) => this.handleApiError(e, pendingAction));
  }

  update(entity, options = {}) {
    const pendingAction = options.apiEndpoint || Actions.update;
    this.addPendingAction(pendingAction, options);
    return this.api[pendingAction](this.prepareApiPayload(entity), options.params).then(({data, ...rest}) => {
      // t('flashMessages.updateSuccess')
      this.addMessage({type: 'success', title: 'flashMessages.updateSuccess'}, options);
      runInAction(() => this.removePendingAction(pendingAction, options));
      if (options.raw) {
        return {data, ...rest};
      }
      return this.replace(this.createModelInstance(data), {preserveReduced: true});
    }).catch((e) => this.handleApiError(e, pendingAction));
  }

  add(entity, {replace = true, replaceOptions = {}} = {}) {
    if (!entity) {
      return null;
    }

    if (!this.getById(entity[this.identityField])) {
      this[this.listName].push(entity);
      return entity;
    }
    return replace && this.replace(entity, replaceOptions);
  }

  addAll(entities, {replace = true, replaceOptions = {}} = {}) {
    const toAdd = [];
    const toReplace = [];
    entities.forEach((entity) => {
      const idx = this[this.listName].findIndex(this.comparator(entity[this.identityField]));
      if (idx >= 0 && replace) {
        toReplace.push({idx, entity});
      } else if (idx === -1) {
        toAdd.push(entity);
      }
    });
    let replaced = [];
    if (replace) {
      // eslint-disable-next-line no-underscore-dangle
      replaced = this._replaceAll(toReplace, replaceOptions);
    }
    this[this.listName].push(...toAdd);
    return toAdd.concat(...replaced);
  }

  getById(id) {
    if (id == null) {
      return null;
    }
    return this[this.listName].find(this.comparator(id));
  }

  getByIds(ids) {
    if (!ids) {
      return [];
    }
    return this[this.listName].filter(this.multiComparator(ids));
  }

  replace(entity, options = {}) {
    const oldEntity = this.getById(entity[this.identityField]);
    if (!oldEntity) {
      return this.add(entity);
    }
    return oldEntity.copyAttributesFrom(entity, options);
  }

  // eslint-disable-next-line no-underscore-dangle
  _replaceAll(entities, options = {}) {
    const result = [];
    entities.forEach(({idx, entity}) => {
      this[this.listName][idx].copyAttributesFrom(entity, options);
      result.push(this[this.listName][idx]);
    });
    return result;
  }

  remove(id) {
    if (!id) {
      return;
    }
    const index = this[this.listName].findIndex(this.comparator(id));
    if (index >= 0) {
      this[this.listName].splice(index, 1);
    }
  }

  clear() {
    if (!this[this.listName]) {
      return;
    }
    this[this.listName].clear();
  }

  get hasPendingRequests() {
    return this.pendingActions.length > 0;
  }

  get isEmpty() {
    return this[this.listName].length === 0;
  }

  get isLoadingCollection() {
    return !!this.isActionInProgress(Actions.loadAll);
  }

  get isLoadingAndEmpty() {
    return this.isLoadingCollection && this.isEmpty;
  }

  get isMutationInProgress() {
    return this.isOneOfActionsInProgress([Actions.create, Actions.update]);
  }

  get entities() {
    return this[this.listName];
  }

  isLoading(id = null) {
    return !!this.pendingActions.find(
      (pendingAction) => pendingAction.action === Actions.load
        && (!id || id === pendingAction[this.identityField])
    );
  }

  isOneOfActionsInProgress(actions, params = null) {
    return actions.some((a) => this.isActionInProgress(a, params));
  }

  isActionInProgress(paramAction, options = null) {
    const index = this.getPendingActionIndex(paramAction, options);
    return index >= 0;
  }

  handleApiError(e, paramAction, options = null) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(e, {action: paramAction});
    }

    if (e.error && e.error.messages) {
      const [errorMessage] = e.error.messages;
      const knownErrors = Object.keys(i18n.t('flashMessages'));
      if (knownErrors.indexOf(errorMessage) >= 0) {
        const message = {type: 'error', title: `flashMessages.${errorMessage}`};
        this.rootStore.flashMessageStore.addFlashMessage(message);
      }
    } else if (e.name && e.name === 'E_BAD_REQUEST') {
      this.rootStore.flashMessageStore.addFlashMessage({type: 'error', title: e.message});
    }

    if (paramAction) {
      runInAction(() => this.removePendingAction(paramAction, options));
    }

    return Promise.reject(ApiError.fromPlainObject(e));
  }

  addMessage(message, {skipNotification, successMessage}) {
    if (!skipNotification) {
      this.rootStore.flashMessageStore.addFlashMessage(successMessage || message);
    }
  }

  createModelInstance(json) {
    const model = this.modelClass.fromPlainObject(json);
    model.populateAttributesFromStore(this.rootStore);
    return model;
  }

  getPendingActionReference(paramAction, options = null) {
    return {
      action: paramAction,
      apiEndpoint: options?.apiEndpoint,
      [this.identityField]: options?.[this.identityField],
      params: options?.params,
    };
  }

  addPendingAction(paramAction, options = null) {
    this.removePendingAction(paramAction, options);
    this.pendingActions.push(this.getPendingActionReference(paramAction, options));
  }

  removePendingAction(paramAction, options = null) {
    const index = this.getPendingActionIndex(paramAction, options);
    if (index >= 0) {
      this.pendingActions.splice(index, 1);
    }
  }

  getPendingActionIndex(paramAction, options = null) {
    const reference = this.getPendingActionReference(paramAction, options);
    return this.pendingActions.findIndex((pendingAction) => isMatch(reference, pendingAction));
  }

  getDifferenceByIds(ids = []) {
    const sameId = (entity) => (id) => id === entity[this.identityField];
    const notInIds = (entity) => ids.findIndex(sameId(entity)) === -1;
    return this[this.listName].filter(notInIds);
  }

  // eslint-disable-next-line class-methods-use-this
  getDependencies() {
    return [];
  }

  // eslint-disable-next-line no-unused-vars,class-methods-use-this
  loadDependencies(dependencies, promise) {
    return promise.then(async (results) => {
      if (!Array.isArray(results)) {
        results = [results];
      }
      if (results) {
        const dependencyPromises = [];
        dependencies.forEach((dependency) => {
          const ids = results.filter((op) => !!op).map((op) => op[dependency.modelId]).flat(1);
          if (ids.length) {
            dependencyPromises.push(dependency.store.loadManyWithDependencies(
              ids,
              dependency.store.getDependencies(),
              {params: dependency.params || {}}
            ));
          }
        });
        await Promise.all(dependencyPromises);
      }

      return results;
    });
  }

  loadWithDependencies(id, paramOptions, dependencies = this.getDependencies()) {
    return this.loadDependencies(dependencies, this.load(id, paramOptions));
  }

  loadAllWithDependencies({
    dependencies = this.getDependencies(),
    ...paramOptions
  }) {
    return this.loadDependencies(dependencies, this.loadAll(paramOptions));
  }

  loadManyWithDependencies(
    ids,
    dependencies = this.getDependencies(),
    paramOptions = {}
  ) {
    return this.loadDependencies(dependencies, this.loadMany(ids, paramOptions));
  }
}

decorate(EntityStore, {
  add: action,
  create: action,
  load: action,
  loadAll: action,
  remove: action,
  replace: action,
  update: action,
  pendingActions: observable,
  hasPendingRequests: computed,
  isEmpty: computed,
  isLoadingAndEmpty: computed,
  isLoadingCollection: computed,
  isMutationInProgress: computed,
  getDifferenceByIds: observable,
  loadAllWithDependencies: action,
});
